/* eslint-disable indent */
import { useContext, useEffect } from 'react';
import { FieldValues } from 'react-hook-form';
import { AllSelectedTableItemsContext } from '@/providers/AllSelectedTableItemsProvider';
import { TFunction } from 'i18next';
import { getPluralString } from '@/utils/string.utils';
import { getLanguage } from '@/utils/language.utils';

interface UseSelectedTableItemsProps<TSchema extends FieldValues> {
  t: TFunction<undefined>;
  data: TSchema[];
  onDeactivate: (data: TSchema[]) => void;
}

export const useSelectedTableItems = <TSchema extends FieldValues>({
  t,
  data,
  onDeactivate,
}: UseSelectedTableItemsProps<TSchema>) => {
  const { isFrench } = getLanguage();

  const {
    allTableItemsSelected,
    setAllTableItemsSelected,
    selectedTableData,
    setSelectedTableData,
  } = useContext(AllSelectedTableItemsContext);

  useEffect(() => {
    setAllTableItemsSelected(false);
    setSelectedTableData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allTableItemsSelected) {
      setSelectedTableData(data);
    } else {
      if (selectedTableData?.length === data?.length) {
        setSelectedTableData([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTableItemsSelected]);

  const numberOfItemsText = `${
    selectedTableData?.length || 0
  } ${getPluralString(
    t('common:item').toLowerCase(),
    selectedTableData?.length
  )} ${
    isFrench
      ? getPluralString(
          t('common:selected').toLowerCase(),
          selectedTableData?.length || 0
        )
      : t('common:selected').toLowerCase()
  }`;

  const onSelectedItem = (id: string) => {
    setSelectedTableData((prevSelectedData) => {
      const foundData = prevSelectedData.find((item) => item.id === id);

      if (foundData) {
        setAllTableItemsSelected(false);
        return prevSelectedData.filter((item) => item.id !== id);
      } else {
        const foundItem = data?.find((item) => item.id === id);

        const selectedObjects = foundItem
          ? [...prevSelectedData, foundItem]
          : prevSelectedData;

        if (selectedObjects?.length === data?.length) {
          setAllTableItemsSelected(true);
        }

        return selectedObjects;
      }
    });
  };

  const handleDeactivate = () => {
    let itemsToDelete = [];
    for (const item of selectedTableData) {
      itemsToDelete = [...itemsToDelete, item];
    }

    onDeactivate(itemsToDelete);
  };

  return {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  };
};
